/**
 * PWA install context (Phase 3, §5.6). Wraps `useInstallPrompt` so the whole app
 * shares one captured `beforeinstallprompt` event: `App` mounts the provider and
 * `InstallPrompt` renders the affordance off `usePwaInstall()`.
 */
import { createContext, use, type ReactNode } from 'react';
import { useInstallPrompt } from '../lib/pwa/useInstallPrompt.ts';

export interface PwaInstall {
  /** True once a `beforeinstallprompt` event has been captured (non-iOS). */
  canInstall: boolean;
  /** True on iOS Safari, where install is a manual "Teilen → Home" hint. */
  isIOS: boolean;
  /** True when already running as an installed standalone app. */
  isStandalone: boolean;
  /** Show the native install prompt. */
  promptInstall: () => Promise<void>;
}

const PwaInstallContext = createContext<PwaInstall | null>(null);

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  const value = useInstallPrompt();
  return <PwaInstallContext value={value}>{children}</PwaInstallContext>;
}

export function usePwaInstall(): PwaInstall {
  const ctx = use(PwaInstallContext);
  if (!ctx) {
    throw new Error('usePwaInstall muss innerhalb von <PwaInstallProvider> verwendet werden.');
  }
  return ctx;
}
